
import { format, formatDistance, differenceInHours, isToday, isTomorrow } from 'date-fns';
import { hu } from 'date-fns/locale';

// Helper to create ISO date strings with Hungarian timezone
export const createDate = (year: number, month: number, day: number, hour = 0, minute = 0): string => {
  const date = new Date(year, month - 1, day, hour, minute);
  return date.toISOString();
};

// Generate random ID
export const generateId = (): string => Math.random().toString(36).substring(2, 9);

// Format date for display
export const formatEventDate = (dateString: string, options: Intl.DateTimeFormatOptions = {}) => {
  const date = new Date(dateString);
  return format(date, 'yyyy. MMMM d.', { locale: hu, ...options });
};

// Format time for display
export const formatEventTime = (dateString: string) => {
  const date = new Date(dateString);
  return format(date, 'HH:mm', { locale: hu });
};

// Check if a time is midnight (00:00)
export const isMidnight = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date.getHours() === 0 && date.getMinutes() === 0;
};

// Helper function to check if time is specified
const isTimeSpecified = (startDate: string, endDate: string): boolean => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return !(start.getHours() === 0 && start.getMinutes() === 0 &&
           end.getHours() === 0 && end.getMinutes() === 0);
};

// Get date range string according to Hungarian format conventions
export const getEventDateRangeString = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Check if the event is today or tomorrow
  if (isToday(start)) {
    // Check if time is specified (not midnight)
    if (!isMidnight(startDate)) {
      return `Ma, ${format(start, 'HH:mm', { locale: hu })} órakor`;
    } else {
      return 'Ma';
    }
  }
  
  if (isTomorrow(start)) {
    // Check if time is specified (not midnight)
    if (!isMidnight(startDate)) {
      return `Holnap, ${format(start, 'HH:mm', { locale: hu })} órakor`;
    } else {
      return 'Holnap';
    }
  }
  
  // Check if it's a single day event
  const isSameDay = start.getDate() === end.getDate() && 
                    start.getMonth() === end.getMonth() && 
                    start.getFullYear() === end.getFullYear();
  
  // Check if startTime and endTime have specific times (nem éjfél)
  const hasTimeSpecified = !isMidnight(startDate) || !isMidnight(endDate);
  
  // Egy napos esemény, pontos időpontokkal
  if (isSameDay && hasTimeSpecified) {
    // Ha ugyanarra az időpontra vannak állítva
    if (start.getHours() === end.getHours() && start.getMinutes() === end.getMinutes()) {
      return `${format(start, 'yyyy. MMMM d., HH:mm', { locale: hu })}`;
    }
    
    // Egyébként mutass időtartamot
    return `${format(start, 'yyyy. MMMM d., HH:mm', { locale: hu })} - ${format(end, 'HH:mm', { locale: hu })}`;
  }
  
  // Egy napos esemény, pontos időpont nélkül
  if (isSameDay && !hasTimeSpecified) {
    return format(start, 'yyyy. MMMM d.', { locale: hu });
  }
  
  // Multi-day event in same month and year
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    // Format: "2025. március 13-16."
    return `${format(start, 'yyyy. MMMM d', { locale: hu })}-${format(end, 'd.', { locale: hu })}`;
  }
  
  // Multi-day event in same year but different months
  if (start.getFullYear() === end.getFullYear()) {
    // Format: "2025. március 29-április 2."
    return `${format(start, 'yyyy. MMMM d', { locale: hu })}-${format(end, 'MMMM d.', { locale: hu })}`;
  }
  
  // Multi-day event in different years
  // Format: "2025. december 30. - 2026. január 2."
  return `${formatEventDate(startDate)} - ${formatEventDate(endDate)}`;
};
